import dateFormat from 'dateformat';
import { IEndState, IRoleType } from '@publishing-engine/domain/models';
import { Flex, Text } from '@chakra-ui/react';
import ContextMenu from 'src/components/Submission/ContextMenu';

type ActionItem = {
  label?: string;
  description?: string;
  headingColor?: string;
  name: string;
  action: () => void;
};

type IParams = {
  dateEnteredCurrentPhase: string;
  reasonForRejection?: string;
};

type RoleContent = Record<
  IRoleType,
  {
    heading: string | ((params: IParams) => string);
    description: string | ((params: IParams) => string | JSX.Element);
  }
>;

const documentPhases: Record<IEndState, RoleContent> = {
  rejected: {
    author: {
      heading: 'Rejected',
      description: (params: IParams) => (
        <Text>
          Your Submission was rejected at{' '}
          {dateFormat(params.dateEnteredCurrentPhase, 'mmm d, yyyy')}. More
          context about this decision is available on the Feedback tab.
        </Text>
      )
    },
    editorial: {
      heading: 'Rejected',
      description: (params: IParams) => (
        <Text textStyle={'textBody'}>
          This Submission was rejected on{' '}
          {dateFormat(params.dateEnteredCurrentPhase, 'mmm d, yyyy')} and is now
          archived. All activity has ceased. For more details, please refer to
          the Additional Information section.
        </Text>
      )
    },
    reviewer: {
      heading: 'Rejected',
      description:
        'The editorial team has gathered enough feedback to advance this submission. No further action is required. Thank you for your willingness to contribute!'
    },
    expert: {
      heading: 'Rejected',
      description:
        'The editorial team has gathered enough feedback to advance this submission. No further action is required. We appreciate your time and effort!'
    }
  },
  accepted: {
    author: {
      heading: 'Accepted',
      description: '' // TODO add content
    },
    editorial: {
      heading: 'Accepted',
      description: '' // TODO add content
    },
    reviewer: {
      heading: 'Accepted',
      description: ''
    },
    expert: {
      heading: '',
      description: ''
    }
  },
  withdrawn: {
    author: {
      heading: 'Withdrawn',
      description: (params: IParams) => (
        <Text textStyle={'textBody'}>
          This Submission was withdrawn on{' '}
          {dateFormat(params.dateEnteredCurrentPhase, 'mmm d, yyyy')} and is now
          archived. All activity has ceased. For more details, please refer to
          the Additional Information section.
        </Text>
      )
    },
    editorial: {
      heading: 'Withdrawn',
      description: (params: IParams) => (
        <Text textStyle={'textBody'}>
          This Submission was withdrawn on{' '}
          {dateFormat(params.dateEnteredCurrentPhase, 'mmm d, yyyy')} and is now
          archived. All activity has ceased. For more details, please refer to
          the Additional Information section.
        </Text>
      )
    },
    reviewer: {
      heading: 'Withdrawn',
      description: (params: IParams) => (
        <Text>
          This Submission was withdrawn on{' '}
          {dateFormat(params.dateEnteredCurrentPhase, 'mmm d, yyyy')} and is now
          archived. All activity has ceased.
        </Text>
      )
    },
    expert: {
      heading: 'Withdrawn',
      description:
        'The editorial team has gathered enough feedback to advance this submission. No further action is required. We appreciate your time and effort!'
    }
  },
  formalAcceptance: {
    author: {
      heading: 'Accepted for Publication',
      description: () => (
        <>
          <Text>
            Congratulations! Your Submission was accepted for publication and
            will be on its way for Production within 24 hours.
          </Text>
          <Text>
            No further action is needed: the Publisher will contact you in case
            you need to provide additional information. You will be notified
            once the Submission is published.
          </Text>
        </>
      )
    },
    editorial: {
      heading: 'Accepted for Publication',
      description:
        'This Submission was forwarded to your production system. The author and Reviewers have been notified via email about this decision.'
    },
    reviewer: {
      heading: 'Accepted for Publication',
      description: () => (
        <>
          <Text>
            This Submission was accepted for publication and will be on its way
            for Production within 24 hours.
          </Text>
          <Text>
            Thank you for your contribution! You will be notified once the
            Submission is published.
          </Text>
        </>
      )
    },
    expert: {
      heading: 'Accepted for Publication',
      description: () => (
        <>
          <Text>
            This Submission was accepted for publication and will be on its way
            for Production within 24 hours.
          </Text>
          <Text>
            Thank you for your contribution! You will be notified once the
            Submission is published.
          </Text>
        </>
      )
    }
  }
};

export function getPhaseContent(
  phase: IEndState,
  role: IRoleType,
  params: IParams
): {
  heading: string;
  description: string | JSX.Element;
} {
  const phaseContent = documentPhases?.[phase]?.[role];

  if (phaseContent) {
    return {
      heading:
        typeof phaseContent.heading === 'string'
          ? phaseContent.heading
          : phaseContent.heading(params),
      description:
        typeof phaseContent.description === 'string'
          ? phaseContent.description
          : phaseContent.description(params)
    };
  }

  return {
    heading: '',
    description: ''
  };
}

export const EndState = ({
  currentWorkflowState,
  role,
  dateEnteredCurrentPhase,
  assessmentStatus,
  actions = []
}: {
  currentWorkflowState: IEndState;
  role: IRoleType;
  dateEnteredCurrentPhase: string;
  assessmentStatus?: JSX.Element | string;
  actions?: ActionItem[];
}) => {
  const phaseData = getPhaseContent(currentWorkflowState as IEndState, role, {
    dateEnteredCurrentPhase
  });

  return (
    <Flex
      flexDirection="column"
      width="100%"
      backgroundColor="surfacePrimaryFill"
    >
      <Flex flexDirection="column">
        {/* ---- Heading --- */}
        <Flex
          alignItems="center"
          gap="2"
          padding={4}
          backgroundColor="surfaceSecondaryFill"
          justifyContent="space-between"
        >
          <Flex alignItems="center" gap="2">
            <Text textStyle="headingItem">{phaseData.heading}</Text>
            {assessmentStatus && assessmentStatus}
          </Flex>
          {!!actions.length && <ContextMenu actions={actions} />}
        </Flex>
        <Flex flexDirection="column" padding={4} gap={4}>
          {phaseData.description}
        </Flex>
      </Flex>
    </Flex>
  );
};
